import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring', stiffness: 100, damping: 18 },
  },
};

const trackCTAClick = (label: string, category: string) => {
  if (typeof window !== 'undefined' && (window as Record<string, unknown>).gtag) {
    (window as Record<string, unknown>).gtag('event', 'cta_click', {
      event_category: category,
      event_label: label,
    });
  }
};

const floatingAnimation = {
  y: [0, -8, 0],
  transition: {
    duration: 4,
    repeat: Infinity,
    repeatType: 'reverse' as const,
    ease: 'easeInOut',
  },
};

const Hero = () => {
  const talents = [
    { name: 'Full Stack Development', icon: 'ri-code-box-line' },
    { name: 'Cloud & DevOps', icon: 'ri-cloud-line' },
    { name: 'Mobile App Development', icon: 'ri-smartphone-line' },
    { name: 'AI & Data Engineering', icon: 'ri-brain-line' },
    { name: 'ERP & Enterprise Applications', icon: 'ri-building-2-line' },
    { name: 'QA & Automation Testing', icon: 'ri-bug-line' },
    { name: 'UI/UX Design', icon: 'ri-palette-line' },
    { name: 'Healthcare IT', icon: 'ri-hospital-line' },
    { name: 'Product Engineering', icon: 'ri-lightbulb-line' },
  ];

  const [hoveredTalent, setHoveredTalent] = useState<number | null>(null);

  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background gradient base */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50/60 via-white to-amber-50/40"></div>

      {/* Decorative blurred orbs */}
      <div className="absolute top-20 left-[-10%] w-[500px] h-[500px] bg-primary-200/30 rounded-full blur-[100px] pointer-events-none"></div>
      <div className="absolute bottom-10 right-[-5%] w-[400px] h-[400px] bg-amber-200/25 rounded-full blur-[90px] pointer-events-none"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-100/20 rounded-full blur-[120px] pointer-events-none"></div>

      {/* Subtle grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgb(15 23 42) 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }}
      ></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 w-full relative z-10">
        <div className="flex flex-col items-center text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ ...springTransition, duration: 0.7 }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-full mb-6 md:mb-8 cursor-pointer hover:border-primary-400 hover:shadow-md hover:shadow-primary-500/10 transition-all duration-300"
          >
            <motion.div
              animate={{ rotate: [0, 15, -15, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            >
              <i className="ri-sparkling-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            </motion.div>
            <span className="text-xs sm:text-sm font-medium text-gray-700">Digital Products & Engineering Teams</span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springTransition, duration: 0.8, delay: 0.1 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6 max-w-5xl"
          >
            We Build Digital Products
            <br />
            <motion.span
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ ...springTransition, duration: 0.8, delay: 0.3 }}
              className="text-primary-600 inline-block"
            >
              & Scale Engineering Teams
            </motion.span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springTransition, duration: 0.7, delay: 0.25 }}
            className="text-base sm:text-lg md:text-xl text-gray-600 mb-8 md:mb-10 max-w-3xl leading-relaxed px-2"
          >
            Delivering custom software development and flexible IT staffing solutions for startups, SMEs, and enterprises.
          </motion.p>

          {/* CTA Buttons + Floating Cards Wrapper */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springTransition, duration: 0.7, delay: 0.35 }}
            className="relative mb-12 md:mb-16 w-full"
          >
            {/* Floating Stat Card - Left: Service Verticals (beside Explore Services) */}
            <Link
              to="/services"
              className="absolute top-1/2 -translate-y-1/2 left-1/3 -translate-x-[calc(50%+10rem)] lg:-translate-x-[calc(50%+14rem)] hidden md:block z-20 cursor-pointer"
            >
              <motion.div
                initial={{ opacity: 0, x: -40, scale: 0.8 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ ...springTransition, duration: 0.8, delay: 0.65 }}
              >
                <motion.div
                  animate={{
                    y: [0, 6, 0],
                    transition: {
                      duration: 5,
                      repeat: Infinity,
                      repeatType: 'reverse' as const,
                      ease: 'easeInOut',
                      delay: 1,
                    },
                  }}
                  whileHover={{ scale: 1.06 }}
                  className="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl shadow-primary-500/10 border border-gray-100 p-4 md:p-5 transition-shadow duration-300 hover:shadow-2xl hover:shadow-primary-500/20"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-50 rounded-xl">
                      <i className="ri-code-box-line text-xl text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-xl md:text-2xl font-bold text-navy-900">15+</div>
                      <div className="text-[10px] md:text-xs text-gray-500 font-medium uppercase tracking-wider">Service Verticals</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                    <span className="text-[10px] md:text-xs font-semibold text-emerald-700">Full-Stack Delivery</span>
                  </div>
                </motion.div>
              </motion.div>
            </Link>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5">
              <Link
                to="/software-development"
                onClick={() => trackCTAClick('Explore Services', 'homepage_hero')}
                className="w-full sm:w-auto px-7 py-3.5 md:px-8 md:py-4 bg-primary-500 text-white text-sm md:text-base font-semibold rounded-lg hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
              >
                Explore Services
                <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300 inline-block"></i>
              </Link>
              <Link
                to="/talent-solutions"
                onClick={() => trackCTAClick('Hire Talent', 'homepage_hero')}
                className="w-full sm:w-auto px-7 py-3.5 md:px-8 md:py-4 bg-transparent border-2 border-navy-900 text-navy-900 text-sm md:text-base font-semibold rounded-lg hover:bg-navy-900 hover:text-white hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap group"
              >
                Hire Talent
                <i className="ri-user-add-line ml-2 group-hover:translate-x-1 transition-transform duration-300 inline-block"></i>
              </Link>
            </div>

            {/* Floating Stat Card - Right: Vetted Engineers (beside Hire Talent) */}
            <Link
              to="/talent-solutions"
              className="absolute top-1/2 -translate-y-1/2 right-1/3 translate-x-[calc(50%+10rem)] lg:translate-x-[calc(50%+14rem)] hidden md:block z-20 cursor-pointer"
            >
              <motion.div
                initial={{ opacity: 0, x: 40, scale: 0.8 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ ...springTransition, duration: 0.8, delay: 0.5 }}
              >
                <motion.div
                  animate={floatingAnimation}
                  whileHover={{ scale: 1.06 }}
                  className="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl shadow-amber-500/10 border border-gray-100 p-4 md:p-5 transition-shadow duration-300 hover:shadow-2xl hover:shadow-amber-500/20"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-xl">
                      <i className="ri-user-star-line text-xl text-amber-600"></i>
                    </div>
                    <div>
                      <div className="text-xl md:text-2xl font-bold text-navy-900">200+</div>
                      <div className="text-[10px] md:text-xs text-gray-500 font-medium uppercase tracking-wider">Vetted Engineers</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 rounded-full">
                    <i className="ri-flashlight-line text-[10px] md:text-xs text-primary-600"></i>
                    <span className="text-[10px] md:text-xs font-semibold text-primary-700">48h Match Time</span>
                  </div>
                </motion.div>
              </motion.div>
            </Link>
          </motion.div>

          {/* Stat Cards Row — elevated credibility strip */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springTransition, duration: 0.6, delay: 0.5 }}
            className="mb-8 md:mb-10 flex flex-wrap items-center justify-center gap-3 sm:gap-4"
          >
            <div className="flex items-center gap-3 bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100 px-4 py-3 hover:shadow-lg hover:border-primary-200 hover:-translate-y-0.5 transition-all duration-300 cursor-default">
              <div className="w-9 h-9 flex items-center justify-center bg-amber-50 rounded-xl">
                <i className="ri-user-star-line text-lg text-amber-600"></i>
              </div>
              <div className="text-left">
                <div className="text-lg font-bold text-navy-900 leading-tight">200+</div>
                <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Vetted Engineers</div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100 px-4 py-3 hover:shadow-lg hover:border-primary-200 hover:-translate-y-0.5 transition-all duration-300 cursor-default">
              <div className="w-9 h-9 flex items-center justify-center bg-primary-50 rounded-xl">
                <i className="ri-flashlight-line text-lg text-primary-600"></i>
              </div>
              <div className="text-left">
                <div className="text-lg font-bold text-navy-900 leading-tight">48h</div>
                <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Match Time</div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100 px-4 py-3 hover:shadow-lg hover:border-primary-200 hover:-translate-y-0.5 transition-all duration-300 cursor-default">
              <div className="w-9 h-9 flex items-center justify-center bg-emerald-50 rounded-xl">
                <i className="ri-shield-check-line text-lg text-emerald-600"></i>
              </div>
              <div className="text-left">
                <div className="text-lg font-bold text-navy-900 leading-tight">98%</div>
                <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Retention</div>
              </div>
            </div>
          </motion.div>

          {/* Talent Categories */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="w-full"
          >
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.5 }}
              className="text-xs sm:text-sm text-gray-500 mb-4 md:mb-6 uppercase tracking-wide"
            >
              Expertise Across Both Pillars
            </motion.p>
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 lg:gap-6"
            >
              {talents.map((talent, index) => (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  onMouseEnter={() => setHoveredTalent(index)}
                  onMouseLeave={() => setHoveredTalent(null)}
                  className={`flex items-center space-x-2 px-3 py-2 bg-white/80 backdrop-blur-sm rounded-lg border transition-all duration-300 cursor-pointer ${
                    hoveredTalent === index
                      ? 'border-primary-400 shadow-lg shadow-primary-500/10 scale-105'
                      : 'border-gray-100 hover:border-primary-300 hover:shadow-md'
                  }`}
                >
                  <motion.div
                    animate={hoveredTalent === index ? { rotate: [0, -10, 10, 0] } : {}}
                    transition={{ duration: 0.4 }}
                    className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center"
                  >
                    <i className={`${talent.icon} text-lg sm:text-xl text-primary-600`}></i>
                  </motion.div>
                  <span className="text-sm font-medium text-gray-700">{talent.name}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.6 }}
            className="mt-12 md:mt-16"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              className="flex flex-col items-center gap-2 cursor-pointer"
              onClick={() => {
                document.getElementById('about')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              <span className="text-xs text-gray-400 uppercase tracking-widest">Scroll to explore</span>
              <div className="w-6 h-10 rounded-full border-2 border-gray-300 flex items-start justify-center p-1.5">
                <motion.div
                  animate={{ y: [0, 12, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                  className="w-1.5 h-1.5 rounded-full bg-primary-500"
                ></motion.div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;